import imageCompression from 'browser-image-compression';

export interface ImageProcessingOptions {
  maxWidthOrHeight?: number;
  maxSizeMB?: number;
  initialQuality?: number;
}

const DEFAULT_OPTIONS: ImageProcessingOptions = {
  maxWidthOrHeight: 1200,
  maxSizeMB: 0.5,
  initialQuality: 0.8,
};

export interface CompressedImageResult {
  file: File;
  dataUrl: string;
}

/**
 * Compress an image file to meet size requirements
 * Also strips EXIF data for privacy (handled by browser-image-compression)
 * Returns both the compressed file and a data URL for preview
 */
export async function compressImage(
  file: File,
  options: ImageProcessingOptions = {}
): Promise<CompressedImageResult> {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  const compressed = await imageCompression(file, {
    maxWidthOrHeight: opts.maxWidthOrHeight,
    maxSizeMB: opts.maxSizeMB,
    initialQuality: opts.initialQuality,
    useWebWorker: true,
    fileType: 'image/jpeg',
    // EXIF is stripped by default in browser-image-compression
  });

  // Generate data URL for preview
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read compressed file'));
    reader.readAsDataURL(compressed);
  });

  return { file: compressed, dataUrl };
}

/**
 * Center-crop an image to a 1:1 square using canvas.
 * Takes a data URL and returns a new File + data URL.
 */
export async function cropToSquare(dataUrl: string): Promise<CompressedImageResult> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const size = Math.min(img.width, img.height);
      const offsetX = (img.width - size) / 2;
      const offsetY = (img.height - size) / 2;

      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      ctx.drawImage(img, offsetX, offsetY, size, size, 0, 0, size, size);

      const croppedDataUrl = canvas.toDataURL('image/jpeg', 0.9);
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to create blob from canvas'));
            return;
          }
          const file = new File([blob], 'plant-photo.jpg', { type: 'image/jpeg' });
          resolve({ file, dataUrl: croppedDataUrl });
        },
        'image/jpeg',
        0.9
      );
    };
    img.onerror = () => reject(new Error('Failed to load image for cropping'));
    img.src = dataUrl;
  });
}

/**
 * Convert a File to base64 string (without data URL prefix)
 */
export async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
      const base64 = result.split(',')[1] ?? '';
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Process an image for upload: compress and convert to base64
 */
export async function processImageForUpload(
  file: File,
  options: ImageProcessingOptions = {}
): Promise<{ base64: string; sizeKB: number }> {
  const { file: compressed } = await compressImage(file, options);
  const base64 = await fileToBase64(compressed);
  const sizeKB = Math.round(compressed.size / 1024);

  return { base64, sizeKB };
}

/**
 * Validate image file type
 */
export function isValidImageType(file: File): boolean {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
  return validTypes.includes(file.type.toLowerCase());
}

/**
 * Create an object URL for previewing an image file
 */
export function createImagePreview(file: File): string {
  return URL.createObjectURL(file);
}

/**
 * Revoke an object URL to free memory
 */
export function revokeImagePreview(url: string): void {
  URL.revokeObjectURL(url);
}

/**
 * Format a file size in bytes to a human-readable string
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Convert an image file to a data URL (includes data:image/... prefix)
 */
export async function imageToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}
